import React from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import * as autoPrintRulesActions from '../../actions/autoPrintRulesActions';
import { useAppDispatch } from 'hooks';
import { AutoPrintRule, PrintMode, PrintModeKey } from 'reducers/autoPrintRulesReducer';
import { Node } from 'reducers/nodesReducer';
import { useSelector } from 'react-redux';
import { getEnabledNodes, getPrinterNames, getReaderNames } from 'reducers/selectors';

const defaultRule: AutoPrintRule = {
  id: 0,
  aeTitle: undefined,
  burnerName: undefined,
  readerName: undefined,
  includePdi: false,
  mode: 'SINGLE_STUDY' as PrintModeKey,
  timeout: 0,
  enabled: true,
};

type TAutoPrintRuleModal = {
  editMode: boolean;
  rule?: AutoPrintRule;
  show: boolean;
  handleClose: () => void;
};

const AutoPrintRuleModal = ({ editMode, rule, show, handleClose }: TAutoPrintRuleModal) => {
  const editRule = rule === undefined ? defaultRule : rule;
  const dispatch = useAppDispatch();
  const readers: string[] = useSelector(getReaderNames);
  const printers: string[] = useSelector(getPrinterNames);
  const nodes: Node[] = useSelector(getEnabledNodes);

  const toaster = {
    success: message => dispatch({ type: 'SUCCESS', message }),
    info: message => dispatch({ type: 'INFO', message }),
    warning: message => dispatch({ type: 'WARNING', message }),
    error: message => dispatch({ type: 'ERROR', message }),
  };

  const schema = yup.object().shape({
    aeTitle: yup
      .string()
      .test('test-node', 'A valid node of origin must be selected', (val: string) => {
        if (val !== undefined) {
          return true;
        }
        return false;
      })
      .required('This field is required'),
    burnerName: yup
      .string()
      .test('test-burner', 'A valid printer must be selected', (val: string) => {
        if (val !== undefined) {
          return true;
        }
        return false;
      })
      .required('This field is required'),
    readerName: yup
      .string()
      .test('test-reader', 'A valid reader must be selected', (val: string) => {
        if (val !== undefined) {
          return true;
        }
        return false;
      })
      .required('This field is required'),
    timeout: yup
      .number()
      .typeError('This field must be a number')
      .min(0, 'This field must be greater or equal to 0')
      .integer()
      .required('This field is required'),
  });

  const editSchema = yup.object().shape({
    burnerName: yup
      .string()
      .test('test-burner', 'A valid printer must be selected', (val: string) => {
        if (printers.includes(val)) {
          return true;
        }
        return false;
      })
      .required('This field is required'),
    readerName: yup
      .string()
      .test('test-reader', 'A valid reader must be selected', (val: string) => {
        if (readers.includes(val)) {
          return true;
        }
        return false;
      })
      .required('This field is required'),
    timeout: yup
      .number()
      .typeError('This field must be a number')
      .required('This field is required')
      .min(0, 'This field must be greater or equal to 0')
      .integer(),
  });

  const submitRule = (values: AutoPrintRule) => {
    if (editMode) {
      axios
        .put(
          `/rest/react/auto-print-rules`,
          {
            id: values.id,
            aeTitle: values.aeTitle,
            burnerName: values.burnerName,
            readerName: values.readerName,
            includePdi: values.includePdi,
            mode: values.mode,
            timeout: values.timeout,
            enabled: values.enabled,
          },
          {
            headers: {
              'Content-type': 'application/json',
            },
          }
        )
        .then(res => {
          dispatch(autoPrintRulesActions.getAutoPrintRules());
          toaster.success(`Rule successfully edited`);
        })
        .catch(e => toaster.error(e.message));
    } else {
      axios
        .post(
          `/rest/react/auto-print-rules`,
          {
            aeTitle: values.aeTitle,
            burnerName: values.burnerName,
            readerName: values.readerName,
            includePdi: values.includePdi,
            mode: values.mode,
            timeout: values.timeout,
            enabled: values.enabled,
          },
          {
            headers: {
              'Content-type': 'application/json',
            },
          }
        )
        .then(res => {
          dispatch(autoPrintRulesActions.getAutoPrintRules());
          toaster.success('Rule added successfully');
        })
        .catch(e => {
          toaster.error(e.message);
        });
    }
  };

  return (
    <React.Fragment>
      <Modal show={show} size="lg" onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>
            {editMode ? (
              <span>You are currently editing an automatic CD print rule</span>
            ) : (
              <span>You are currently adding a new automatic CD print rule</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            validateOnChange={true}
            validationSchema={editMode ? editSchema : schema}
            onSubmit={(values, actions) => {
              submitRule(values);
              actions.setSubmitting(false);
              handleClose();
            }}
            validateOnMount={editMode}
            initialValues={editRule}
            initialTouched={{
              aeTitle: editMode,
              burnerName: editMode,
              readerName: editMode,
              timeout: editMode,
            }}
          >
            {({ handleSubmit, handleChange, setFieldValue, values, touched, errors }) => (
              <Form id="rule-form" onSubmit={handleSubmit} encType="multipart/form-data">
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Node
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      size="sm"
                      as="select"
                      defaultValue={editMode ? values.aeTitle : undefined}
                      onChange={handleChange}
                      name="aeTitle"
                      isInvalid={touched.aeTitle && !!errors.aeTitle}
                    >
                      {editMode ? (
                        <option value={editRule.aeTitle} disabled>
                          {editRule.aeTitle}
                        </option>
                      ) : (
                        <option value={undefined} disabled selected={true}>
                          Select origin node...
                        </option>
                      )}
                      {nodes.map(node => {
                        return (
                          <option key={'auto-print-node-' + node.aeTitle + '-option'} value={node.aeTitle}>
                            {node.aeTitle}
                          </option>
                        );
                      })}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.aeTitle}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Printer
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      as="select"
                      size="sm"
                      onChange={handleChange}
                      defaultValue={editMode ? values.burnerName : undefined}
                      name="burnerName"
                      isInvalid={touched.burnerName && !!errors.burnerName}
                    >
                      {editMode ? (
                        <option value={editRule.burnerName} disabled>
                          {editRule.burnerName}
                        </option>
                      ) : (
                        <option value={undefined} disabled selected={true}>
                          Select printer...
                        </option>
                      )}
                      {printers.map(printer => {
                        return (
                          <option key={printer + '-option'} value={printer}>
                            {printer}
                          </option>
                        );
                      })}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.burnerName}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Reader
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      as="select"
                      size="sm"
                      onChange={handleChange}
                      defaultValue={editMode ? values.readerName : undefined}
                      name="readerName"
                      isInvalid={touched.readerName && !!errors.readerName}
                    >
                      {editMode ? (
                        <option value={editRule.readerName} disabled>
                          {editRule.readerName}
                        </option>
                      ) : (
                        <option value={undefined} disabled selected={true}>
                          Select reader...
                        </option>
                      )}
                      {readers.map(reader => {
                        return (
                          <option key={reader + '-option'} value={reader}>
                            {reader}
                          </option>
                        );
                      })}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.readerName}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Mode
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control as="select" size="sm" onChange={handleChange} value={values.mode} name="mode">
                      {Object.entries(PrintMode).map(([enumKey, enumValue]) => (
                        <option key={'auto-rule-mode-' + enumKey} value={enumKey}>
                          {enumValue}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Timeout (in minutes)
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      size="sm"
                      onChange={handleChange}
                      value={values.timeout}
                      name="timeout"
                      isInvalid={touched.timeout && !!errors.timeout}
                    ></Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.timeout}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Include pdi
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Check
                      custom
                      id="includePdi-check"
                      label=""
                      type="checkbox"
                      onChange={e => setFieldValue('includePdi', e.target.checked)}
                      checked={values.includePdi}
                      name="includePdi"
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Enabled
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Check
                      custom
                      id="enableRule-check"
                      label=""
                      type="checkbox"
                      onChange={e => setFieldValue('enabled', e.target.checked)}
                      checked={values.enabled}
                      name="enabled"
                    />
                  </Col>
                </Form.Group>
              </Form>
            )}
          </Formik>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          {editMode ? (
            <Button variant="primary" type="submit" form="rule-form">
              Submit edit
            </Button>
          ) : (
            <Button variant="primary" type="submit" form="rule-form">
              Add rule
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default AutoPrintRuleModal;
